<template>
  <div v-bind="$attrs">
    <label>{{ label }}</label>
    <div class="so-time-slot-picker-container">
      <SoDatePicker
        v-model="selectedDate"
        :allowed-dates="allowedDates"
        :error="error"
        type="timeslot"
      />
      <SoRadioGroup
        v-model="selectedSlot"
        :label="formatDateString(selectedDate)"
        :items="items"
        :clearable="clearable"
        :disabled="disabled"
        panel
      />
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoDatePicker from "./SoDatePicker.vue"
  import SoRadioGroup from "./SoRadioGroup.vue"

  export default defineComponent({
    components: {
      SoDatePicker,
      SoRadioGroup,
    },
    inheritAttrs: false,
    props: {
      modelValue: {
        type: [Array, null] as PropType<Date[] | null>,
        default: null,
      },
      timeslots: {
        type: [Array, null] as PropType<Date[][] | null>,
        default: null,
      },
      label: {
        type: String,
        default: null,
      },
      allowPastDates: {
        type: Boolean,
        default: false,
      },
      error: {
        type: String,
        default: null,
      },
      clearable: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        selectedDate: null as null | Date,
        selectedSlot: null as null | string,
      }
    },
    computed: {
      internalValue: {
        get() {
          return this.modelValue
        },
        set(newValue: Date[] | null) {
          this.$emit("update:modelValue", newValue)
        },
      },
      uniqueTimeslots() {
        if (this.timeslots) {
          return [
            ...new Set(
              this.timeslots.map((slot) => this.formatDateArrayString(slot)),
            ),
          ].map((slot) => slot.split(",").map((slot) => new Date(slot)))
        } else return []
      },
      processedTimeslots() {
        return this.uniqueTimeslots
          .filter(
            (slot) =>
              slot.length === 2 &&
              slot[0].getDay() === slot[1].getDay() &&
              (this.allowPastDates ? true : slot[0].getTime() >= Date.now()) &&
              (this.allowPastDates ? true : slot[1].getTime() >= Date.now()),
          )
          .map((slot) => slot.sort())
          .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      },
      allowedDates() {
        return this.processedTimeslots.flat()
      },
      availableSlots() {
        return this.selectedDate
          ? this.processedTimeslots.filter(
              (slot) =>
                slot[0].toDateString() === this.selectedDate?.toDateString(),
            )
          : null
      },
      items() {
        return this.availableSlots?.map((slot) => ({
          text: this.formatSlotLabelString(slot),
          value: this.formatDateArrayString(slot),
        }))
      },
    },
    watch: {
      modelValue(value) {
        this.setInternalValues(value)
      },
      processedTimeslots() {
        if (this.processedTimeslots.length === 0 && this.modelValue) {
          this.internalValue = null
        }
      },
    },
    mounted() {
      this.setInternalValues(this.internalValue)
      this.applySelectedDateWatcher()
      this.applySelectedSlotWatcher()
    },
    methods: {
      setInternalValues(value: Date[] | null) {
        if (value instanceof Array) {
          this.selectedDate = value ? value[0] : null
          this.selectedSlot = value ? this.formatDateArrayString(value) : ""
        }
      },
      applySelectedDateWatcher() {
        this.$watch("selectedDate", (newValue, oldValue) => {
          const modelValueDateString = this.modelValue
            ? this.modelValue[0].toDateString()
            : ""
          if (
            newValue?.toDateString() !== oldValue?.toDateString() &&
            newValue?.toDateString() !== modelValueDateString
          ) {
            this.selectedSlot = ""
          }
        })
      },
      applySelectedSlotWatcher() {
        this.$watch("selectedSlot", (value) => {
          value
            ? (this.internalValue = value
                .split(",")
                .map((slot: string) => new Date(slot)))
            : (this.internalValue = null)
        })
      },
      formatDateArrayString(dateArray: Date[]) {
        return dateArray.flat().toString()
      },
      formatDateString(date: Date | null) {
        return date instanceof Date ? date.toDateString() : ""
      },
      formatSlotLabelString(slot: Date[]) {
        return `${this.formatAmPm(slot[0])} - ${this.formatAmPm(slot[1])}`
      },
      formatAmPm(time: Date) {
        return time.toLocaleString("en-US", {
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        })
      },
    },
  })
</script>

<style scoped>
  .so-time-slot-picker-container {
    display: flex;
    gap: 12px;
  }
</style>
